import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useCreateTask } from '../contexts/tasks';
import { CREATE_STEPS, EMPTY_VALUES, toCreatePayload, validate } from '../utils/form';
import FormSteps, { errorsForStep } from '../../../common/components/form-steps/form-steps';
import TaskFields from './fields';

import '../../clients/styles/form.css';

export default function CreateTaskForm() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const createTask = useCreateTask();

    // Arriving from a tier's task list with ?tier=N lands with that tier
    // already chosen.
    const [values, setValues] = useState(() => ({
        ...EMPTY_VALUES,
        tier_id: params.get('tier') || '',
    }));
    const [errors, setErrors] = useState({});
    const [step, setStep] = useState(0);

    const isLast = step === CREATE_STEPS.length - 1;

    // Forward validates ONLY this step's fields: an error has to appear next
    // to the field that caused it, not three screens later. Backward validates
    // nothing — nothing has been sent yet.
    const next = () => {
        const found = errorsForStep(validate(values, { withPlacement: true }), CREATE_STEPS[step].fields);
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }
        setStep(s => s + 1);
    };

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Enter on any earlier step advances instead of sending: a form with a
        // single visible button teaches the keyboard the wrong thing.
        if (!isLast) {
            next();
            return;
        }

        const found = validate(values, { withPlacement: true });
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            const created = await createTask.mutateAsync(toCreatePayload(values));
            toast.success('Tarea creada');
            navigate(`/admin/tasks/${created.id}`, { replace: true });
        } catch (err) {
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo crear la tarea');
        }
    };

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">Nueva tarea</h2>
                <p className="client-form__subtitle">
                    La unidad que un agente toma. Nace propuesta: quién la toma y
                    cuándo pasa a hecha no se decide desde acá.
                </p>
            </header>

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <FormSteps steps={CREATE_STEPS} current={step} onStep={setStep} />

                    <div className="client-form__section">
                        <h3 className="client-form__section-title">Datos de la tarea</h3>
                        <p className="client-form__section-text">Dónde vive y qué hay que hacer.</p>
                    </div>

                    <TaskFields
                        visible={CREATE_STEPS[step].fields}
                        values={values}
                        errors={errors}
                        onChange={change}
                        disabled={createTask.isPending}
                    />
                </div>

                <div className="client-form__actions">
                    <Link to="/admin/tasks" className="client-form__cancel">Cancelar</Link>
                    <button type="submit" className="client-form__submit" disabled={createTask.isPending}>
                        {createTask.isPending ? 'Creando...' : 'Crear tarea'}
                    </button>
                </div>
            </form>
        </section>
    );
}
